'use client';

// UiI18n — runtime interface translation layer + language switcher.
//
// Why a runtime layer? The app has hundreds of UI strings across dozens of large
// pages. Rather than hand-wrap every string, this translates the RENDERED DOM on
// the fly into the chosen language, using a cache (localStorage + the server's
// DB-backed AI translation cache). Each unique label is translated by the AI once,
// then served from cache forever — so the whole tool localizes, new pages included,
// with zero manual dictionary upkeep.
//
// Switching language sets a flag and reloads; on load, if the language isn't
// English, we translate. Switching back to English just reloads (React renders
// English natively) — no fragile "undo" logic.

import { useEffect, useState, useRef } from 'react';

// The tool's four operating languages. The runtime translator localizes the
// whole interface into any of these; switching also drives the Content Output
// Language so AI-written outreach, blogs and example templates follow too.
const LANGS = [
    { code: 'en', label: 'EN', flag: '🇬🇧', name: 'English' },
    { code: 'de', label: 'DE', flag: '🇩🇪', name: 'Deutsch' },
    { code: 'es', label: 'ES', flag: '🇪🇸', name: 'Español' },
    { code: 'fr', label: 'FR', flag: '🇫🇷', name: 'Français' },
];;

// Right-to-left languages — none of the four operating languages are RTL, but
// keep the guard so the layout is always set explicitly to ltr.
const RTL = new Set();

const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'CODE', 'PRE', 'TEXTAREA', 'CANVAS', 'SVG']);
const CHUNK = 20;            // smaller batches → fewer dropped lines from the model
const DICT_VER = 'v2';      // bump to abandon an older/poisoned client cache

function getLang() {
    if (typeof window === 'undefined') return 'en';
    return localStorage.getItem('uiLang') || 'en';
}

// Decide whether a piece of text is a translatable UI label (vs a number, URL,
// code, id, or user-entered data we must leave alone).
function translatable(t) {
    if (!t || t.length < 2 || t.length > 600) return false;
    if (!/[A-Za-z]/.test(t)) return false;                 // must contain a letter
    if (/^https?:\/\//i.test(t)) return false;             // URL
    if (/^[\w.+-]+@[\w.-]+\.[a-z]{2,}$/i.test(t)) return false; // email
    if (/^[\d\s.,:;/%$€£+()\-–—]+$/.test(t)) return false; // pure number/symbols
    if (/^[A-Za-z0-9_]+$/.test(t) && t.length > 24) return false; // long token/id
    return true;
}

export default function UiI18n() {
    const [lang, setLang] = useState('en');
    const [busy, setBusy] = useState(false);
    const [open, setOpen] = useState(false);
    const state = useRef({ dict: {}, values: new Set(), running: false, dirty: false, observer: null, passTimer: null });

    // Load persisted language + dictionary on mount, then translate if needed.
    useEffect(() => {
        const l = getLang();
        setLang(l);
        // English is the SOURCE language — React already renders English natively,
        // so there is zero translation work to do. Return early and never install
        // the MutationObserver, so English users pay no runtime cost at all.
        if (l === 'en') { document.documentElement.dir = 'ltr'; return; }
        document.documentElement.lang = l;
        document.documentElement.dir = RTL.has(l) ? 'rtl' : 'ltr';
        try {
            const cached = JSON.parse(localStorage.getItem('i18nDict_' + DICT_VER + '_' + l) || '{}');
            state.current.dict = cached || {};
            state.current.values = new Set(Object.values(cached || {}));
        } catch { /* ignore */ }

        const runSoon = () => schedulePass(l);
        const t1 = setTimeout(runSoon, 350);
        const t2 = setTimeout(runSoon, 1400);
        const t3 = setTimeout(runSoon, 3500);

        // Re-translate dynamically rendered content (tab switches, AI results…).
        // Batch mutations onto a single trailing timer (instead of scheduling a pass
        // on every individual mutation) so a burst of DOM churn on big pages (Lead
        // Finder, Autopilot Outreach) collapses into one debounced pass.
        const obs = new MutationObserver(() => schedulePass(l));
        obs.observe(document.body, { childList: true, subtree: true, characterData: true });
        state.current.observer = obs;

        return () => {
            clearTimeout(t1); clearTimeout(t2); clearTimeout(t3);
            if (state.current.passTimer) clearTimeout(state.current.passTimer);
            obs.disconnect();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Trailing debounce for translation passes. A storm of mutations only ever
    // results in ONE pass ~400ms after the DOM settles. When the browser supports
    // requestIdleCallback we run the pass during idle time so we never compete with
    // rendering; otherwise we fall back to a plain timeout.
    function schedulePass(l) {
        const s = state.current;
        if (s.passTimer) clearTimeout(s.passTimer);
        s.passTimer = setTimeout(() => {
            s.passTimer = null;
            if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
                window.requestIdleCallback(() => runPass(l), { timeout: 1200 });
            } else {
                runPass(l);
            }
        }, 400);
    }

    function persist(l) {
        try { localStorage.setItem('i18nDict_' + DICT_VER + '_' + l, JSON.stringify(state.current.dict)); } catch { /* quota */ }
    }

    // Per-node "already translated" memory so repeat passes skip work entirely.
    // Text nodes can't hold attributes, so they're tracked in a WeakSet; elements
    // (attr/value targets) are marked with a data attribute that survives in the DOM.
    const doneTextNodes = (state.current.doneTextNodes ||= new WeakSet());

    // Gather every translatable target currently in the DOM. Nodes we've already
    // translated in a prior pass are skipped up front (WeakSet / data-attr), so a
    // re-translation pass on a big page only looks at genuinely new content.
    function collectTargets() {
        const s = state.current;
        const targets = [];
        // Text nodes
        const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, {
            acceptNode(node) {
                if (doneTextNodes.has(node)) return NodeFilter.FILTER_REJECT;
                const p = node.parentElement;
                if (!p || SKIP_TAGS.has(p.tagName)) return NodeFilter.FILTER_REJECT;
                if (p.closest('[data-no-i18n]')) return NodeFilter.FILTER_REJECT;
                const core = (node.nodeValue || '').trim();
                if (!translatable(core) || s.values.has(core)) return NodeFilter.FILTER_REJECT;
                return NodeFilter.FILTER_ACCEPT;
            }
        });
        let n;
        while ((n = walker.nextNode())) targets.push({ kind: 'text', node: n, core: n.nodeValue.trim() });
        // Attributes: placeholder / title / aria-label, and button/submit values
        document.querySelectorAll('[placeholder],[title],[aria-label]').forEach(el => {
            if (el.closest('[data-no-i18n]')) return;
            if (el.getAttribute('data-i18n-attr-done') === '1') return;
            ['placeholder', 'title', 'aria-label'].forEach(attr => {
                const v = el.getAttribute(attr);
                const core = (v || '').trim();
                if (core && translatable(core) && !s.values.has(core)) targets.push({ kind: 'attr', el, attr, core });
            });
        });
        document.querySelectorAll('input[type=button],input[type=submit]').forEach(el => {
            if (el.closest('[data-no-i18n]')) return;
            if (el.getAttribute('data-i18n-val-done') === '1') return;
            const core = (el.value || '').trim();
            if (core && translatable(core) && !s.values.has(core)) targets.push({ kind: 'val', el, core });
        });
        return targets;
    }

    function applyTarget(tgt, translated) {
        if (!translated) return;
        if (tgt.kind === 'text') {
            const raw = tgt.node.nodeValue || '';
            const m = raw.match(/^(\s*)([\s\S]*?)(\s*)$/);
            tgt.node.nodeValue = (m ? m[1] : '') + translated + (m ? m[3] : '');
            try { doneTextNodes.add(tgt.node); } catch { /* ignore */ }
        } else if (tgt.kind === 'attr') {
            tgt.el.setAttribute(tgt.attr, translated);
            try { tgt.el.setAttribute('data-i18n-attr-done', '1'); } catch { /* ignore */ }
        } else if (tgt.kind === 'val') {
            tgt.el.value = translated;
            try { tgt.el.setAttribute('data-i18n-val-done', '1'); } catch { /* ignore */ }
        }
    }

    async function fetchTranslations(strings, l) {
        try {
            const res = await fetch('/api/i18n/translate-ui', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ strings, lang: l }),
            });
            const data = await res.json();
            return (data && data.success && data.map) ? data.map : {};
        } catch { return {}; }
    }

    // Synchronously apply every cached translation to the CURRENT DOM. Re-collects
    // fresh nodes each time (so it's immune to React re-renders) and brackets its
    // own DOM writes with observer disconnect/reconnect to avoid self-trigger loops.
    function applyCached() {
        const s = state.current;
        if (s.observer) s.observer.disconnect();
        try {
            const targets = collectTargets();
            for (const tgt of targets) {
                const tr = s.dict[tgt.core];
                if (tr != null) applyTarget(tgt, tr);
            }
        } finally {
            if (s.observer) s.observer.observe(document.body, { childList: true, subtree: true, characterData: true });
        }
    }

    async function runPass(l) {
        const s = state.current;
        if (s.running) { s.dirty = true; return; }
        s.running = true;
        try {
            // 1) Apply whatever we already know — instant on cached loads.
            applyCached();
            // 2) Find still-untranslated strings currently on screen and fetch them.
            const unknown = new Set();
            for (const tgt of collectTargets()) {
                if (s.dict[tgt.core] == null) unknown.add(tgt.core);
            }
            const list = [...unknown];
            if (list.length) {
                setBusy(true);
                for (let i = 0; i < list.length; i += CHUNK) {
                    const chunk = list.slice(i, i + CHUNK);
                    const map = await fetchTranslations(chunk, l);
                    for (const k of Object.keys(map)) {
                        s.dict[k] = map[k];
                        s.values.add(map[k]);
                    }
                    persist(l);
                    // Re-apply against the LIVE DOM after each batch so progress shows
                    // immediately and we never write to nodes that React has replaced.
                    applyCached();
                }
                setBusy(false);
            }
        } finally {
            s.running = false;
            if (s.dirty) { s.dirty = false; schedulePass(l); }
        }
    }

    async function switchLang(code) {
        localStorage.setItem('uiLang', code);
        // Keep the whole tool in one language: the interface language also drives
        // the Content Output Language, so AI-written outreach, sequences, blogs and
        // the example templates all come out in the chosen language too. (Users who
        // want them decoupled can still override Content Output Language in Settings.)
        try {
            await fetch('/api/settings', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ contentLanguage: code }),
            });
        } catch { /* non-fatal — UI language still switches */ }
        // Reload so the page re-renders in English first, then the layer (re)applies
        // the chosen language from cache (instant after the first time).
        window.location.reload();
    }

    const current = LANGS.find(o => o.code === lang) || LANGS[0];

    return (
        // Compact, collapsible control tucked into the right margin so it never
        // covers page CTAs. Click the globe to open the language menu.
        <div data-no-i18n style={{ position: 'fixed', bottom: '20px', right: '92px', zIndex: 10000 }}>
            <button
                onClick={() => setOpen(o => !o)}
                title="Interface language"
                style={{
                    display: 'flex', alignItems: 'center', gap: '3px', cursor: 'pointer',
                    background: 'rgba(255,255,255,0.92)', border: '1px solid #e4e9f2', borderRadius: '10px',
                    padding: '6px 8px', color: '#0f172a', fontSize: '13px', fontWeight: 700,
                    backdropFilter: 'blur(8px)', boxShadow: '0 4px 16px rgba(15,23,42,0.12)',
                }}
            >
                <span style={{ fontSize: '15px', lineHeight: 1 }}>{busy ? '…' : current.flag}</span>
                <span style={{ fontSize: '11px', letterSpacing: '0.03em' }}>{current.label}</span>
                <span style={{ fontSize: '9px', color: '#475569' }}>▾</span>
            </button>
            {open && (
                <div style={{
                    position: 'absolute', bottom: 'calc(100% + 6px)', right: 0, display: 'flex', flexDirection: 'column', gap: '2px',
                    background: 'rgba(255,255,255,0.98)', border: '1px solid #e4e9f2', borderRadius: '10px', padding: '4px',
                    backdropFilter: 'blur(8px)', boxShadow: '0 12px 32px rgba(15,23,42,0.16)', minWidth: '160px',
                    maxHeight: '60vh', overflowY: 'auto',
                }}>
                    {LANGS.map(o => (
                        <button
                            key={o.code}
                            onClick={() => { setOpen(false); if (o.code !== lang) switchLang(o.code); }}
                            style={{
                                display: 'flex', alignItems: 'center', gap: '8px', border: 'none', cursor: 'pointer', borderRadius: '7px',
                                padding: '7px 10px', fontSize: '13px', fontWeight: 700, textAlign: 'left', whiteSpace: 'nowrap',
                                background: o.code === lang ? 'linear-gradient(135deg,#2563eb,#1d4ed8)' : 'transparent',
                                color: o.code === lang ? 'white' : '#334155',
                            }}
                        >
                            {o.flag} {o.name || o.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
